import { getNestedSubelementsByType, getPersonName, getPersonRelationships } from '@fs/zion-sls'
import { i18n } from '@fs/zion-locale'
import { MAKE_NEW_RECORD } from '../utils/recordConstants'
import { NO_RELATION_TYPE } from './relationshipConstants'

// Group and relationship reads for the Relationships card.

/**
 * The other people in a person's group, each with their relationship to them. Members with nothing
 * recorded get a no-relation entry, so every member gets a row.
 *
 * @param {Object} params - The parameters object.
 * @param {Object} params.person - The focused person object.
 * @param {string|number} params.recordId - The ID of the focused person's record.
 * @param {Array<Object>} params.allRecords - Array of all image records.
 * @param {Array<Object>} params.images - Array of SLS images.
 * @param {string} params.currentPartitionKey - The partition key of the focused person.
 * @param {Function} params.noRelationLabel - Resolves the label for a pair with nothing recorded
 *   between them.
 * @returns {{relationships: Array<Object>, record: Object|null}} The group's relationships, and the
 *   record itself — null when the person is in no group at all.
 */
export const getPersonsAndRelationshipsForHousehold = ({
  person,
  recordId,
  allRecords,
  images,
  currentPartitionKey,
  noRelationLabel,
}) => {
  // A person bound for a record that does not exist yet has no group to read.
  if (recordId === MAKE_NEW_RECORD) {
    return { relationships: [], record: { id: recordId, partitionKey: currentPartitionKey } }
  }

  const record = allRecords.find((rec) => rec.id === recordId)

  // No record found means no group, so there are no persons to process.
  if (!record) return { relationships: [], record: null }

  // Existing relationships for the focused person, with gendered labels (Father/Mother instead of
  // Parent) when the other person's sex is known (FSS-14997). Edit-mode matching is relKey-based,
  // so gendered display labels are safe.
  const existingRelationshipsMap = new Map()
  getPersonRelationships(person, images, false).forEach((rel) => {
    existingRelationshipsMap.set(rel.otherPersonId, rel)
  })

  const otherRecordPersons =
    getNestedSubelementsByType(record, 'PERSON')?.filter((recordPerson) => recordPerson?.id !== person?.id) || []

  const relationships = otherRecordPersons.map((recordPerson) => {
    const existingRel = existingRelationshipsMap.get(recordPerson.id)

    // If an existing relationship is found, merge it with the person info...
    if (existingRel) {
      return { ...existingRel, person: recordPerson }
    }

    // ...otherwise nothing is recorded between the two.
    return {
      fieldType: NO_RELATION_TYPE,
      relKey: NO_RELATION_TYPE,
      label: noRelationLabel(),
      value: getPersonName({ person: recordPerson, justOneName: true }),
      id: null,
      otherPersonId: recordPerson.id,
      person: recordPerson,
    }
  })

  return { relationships, record }
}

/**
 * Generates a list of household suggestions from the provided records.
 * Each suggestion represents a household, identified by a primary person (if available) or the first person in the record.
 *
 * @param {Object} params - The parameters object.
 * @param {Array<Object>} params.records - Array of SLS records.
 * @param {Object} [params.person] - Optional person object to create a "New Household" suggestion.
 * @returns {Array<{ key: string, primaryText: string }>} An array of household suggestion objects,
 * each containing a unique key and the primary person's name as primaryText.
 */
export const getHouseholdSuggestions = ({ records = [], person }) => {
  const households = new Set()

  // Add the "New Household" option if the person is new or has no recordId
  const newHousehold =
    person && (!person?.recordId || person?.recordId === MAKE_NEW_RECORD)
      ? {
          key: MAKE_NEW_RECORD,
          primaryText: i18n.t('household-details-card.new-household', 'New Household: {personName}', {
            personName: getPersonName({ person, justOneName: true }),
          }),
          partitionKey: person?.partitionKey,
        }
      : null

  if (newHousehold) {
    households.add(newHousehold)
  }

  records.forEach((record) => {
    const recordPersons = getNestedSubelementsByType(record, 'PERSON')
    // Select primary person or first as representative of the household
    const primaryPerson = recordPersons.find((recordPerson) => recordPerson.primary) || recordPersons[0]

    if (primaryPerson) {
      const personName = getPersonName({ person: primaryPerson, justOneName: true })
      households.add({
        key: record.id,
        primaryText: personName,
        record,
        partitionKey: record.partitionKey,
      })
    }
  })

  return Array.from(households)
}
